import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { testExecutionsGridConfig } from '@/features/executions';
import { formatExecutionDuration } from '@/features/executions/formatters/executionFormatters';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { resolveEntityTags } from '@/features/shared/tags';
import { formatStatusLabel, getStatusTextColor, getStatusToneTextColor, Text, Tooltip } from '@/ui';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { ProjectCell, RunLinkCell, TableTextCell, TagsInlineCell } from '@/ui/composites/table';
import { resolveIteratorLabel } from '@/ui/composites/table/cellContents/shared';
import { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { type GridColDef, type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestExecutionDto } from '@flowqa/shared';

const columnFlex = {
  run: 0.5,
  suite: 1,
  test: 1,
  status: 0.5,
  duration: 0.5,
  executed: 0.5,
  executionRun: 1,
  tags: 0.75,
} as const;
const NUMERIC_TEXT_SX = {};
const mapChipKeyToStatusTone = (
  key: ReturnType<typeof getThresholdChipKey>,
): 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal' => {
  if (key === 'yellow') return 'orange';
  if (key === null) return 'neutral';
  return key;
};

type TestExecutionsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<TestExecutionDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: TestExecutionDto[]) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showSuiteColumn?: boolean;
  showTestColumn?: boolean;
  showExecutedColumn?: boolean;
  showExecutionRunColumn?: boolean;
};

export const TestExecutionsTable = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  noRowsText = 'No test executions found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onDataUpdatedAt,
  showLastUpdated,
  showSuiteColumn = true,
  showTestColumn = true,
  showExecutedColumn = true,
  showExecutionRunColumn = true,
}: TestExecutionsTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const durationThreshold = thresholds.tests.avgDuration;
  const columns: Array<GridColDef<TestExecutionDto>> = useMemo(() => {
    const result: Array<GridColDef<TestExecutionDto>> = [];

    result.push({
      field: 'runNumber',
      headerName: 'Run',
      flex: columnFlex.run,
      align: 'left',
      headerAlign: 'left',
      filterable: false,
      sortable: false,
      renderCell: (params): ReactElement => {
        const runNumber = params.row.runNumber;
        const href =
          params.row.runNumber && (params.row.testSlug ?? params.row.testId)
            ? `/tests/${params.row.testSlug ?? params.row.testId}/runs/${params.row.runNumber}`
            : '/tests';
        const isLatest = params.row.isLatest === true;
        return (
          <RunLinkCell
            label={typeof runNumber === 'number' ? resolveIteratorLabel(runNumber) : 'View'}
            href={href}
            latest={isLatest}
          />
        );
      },
    });

    if (showSuiteColumn) {
      result.push({
        field: 'suiteName',
        headerName: 'Suite',
        flex: columnFlex.suite,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value, row) => row.suiteName ?? row.suiteId ?? 'Suite',
        renderCell: (params) => (
          <TableTextCell
            primary={{
              label: params.row.suiteName ?? params.row.suiteId ?? 'Suite',
              href: params.row.suiteSlug ? `/suites/${params.row.suiteSlug}` : '/suites',
              onClick: () => {
                setSelectedProjectId(params.row.projectId);
              },
            }}
          />
        ),
      });
    }

    if (showTestColumn) {
      result.push({
        field: 'testName',
        headerName: 'Test',
        flex: columnFlex.test,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value, row) => row.testName ?? row.name ?? row.testId,
        renderCell: (params) => (
          <TableTextCell
            primary={{
              label: params.row.testName ?? params.row.name ?? params.row.testId,
              href: `/tests/${params.row.testSlug ?? params.row.testId}`,
              onClick: () => {
                setSelectedProjectId(params.row.projectId);
              },
            }}
          />
        ),
      });
    }

    result.push({
      field: 'status',
      headerName: 'Status',
      flex: columnFlex.status,
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      renderCell: (params) => {
        const status = params.row.status;
        const label = formatStatusLabel(status);
        return (
          <Text
            role="body"
            component="span"
            sx={{
              color: (theme) => getStatusTextColor(theme, status),
            }}
            transform="uppercase"
          >
            {label.length > 0 ? label : '-'}
          </Text>
        );
      },
    });

    result.push({
      field: 'durationMs',
      headerName: 'Duration',
      flex: columnFlex.duration,
      align: 'right',
      headerAlign: 'right',
      filterable: false,
      sortable: true,
      renderCell: (params) => {
        const durationMs = params.row.durationMs ?? null;
        const hasDuration = durationMs !== null;
        const durationSeconds = hasDuration ? durationMs / 1000 : null;
        const chipKey = hasDuration
          ? getThresholdChipKey(durationSeconds, durationThreshold)
          : null;
        const tone = mapChipKeyToStatusTone(chipKey);
        return (
          <Text
            role="body"
            component="span"
            sx={[
              NUMERIC_TEXT_SX,
              {
                color: hasDuration
                  ? chipKey === null
                    ? 'text.primary'
                    : (theme) => getStatusToneTextColor(theme, tone)
                  : 'textSecondary',
              },
            ]}
          >
            {formatExecutionDuration(durationMs)}
          </Text>
        );
      },
    });

    if (showExecutedColumn) {
      result.push({
        field: 'timestamp',
        headerName: 'Executed',
        flex: columnFlex.executed,
        align: 'right',
        headerAlign: 'right',
        filterable: false,
        renderCell: (params) => {
          const label = formatRelativeTime(params.row.timestamp);
          const fullTimestamp = formatDate(params.row.timestamp, { includeTime: true });
          return (
            <Tooltip
              title={fullTimestamp}
              disableHoverListener={!fullTimestamp}
              arrow
              slotProps={{
                tooltip: { sx: { fontSize: (theme) => theme.typography.body2.fontSize } },
              }}
            >
              <span>
                <Text role="body" component="span" sx={NUMERIC_TEXT_SX}>
                  {label.length > 0 ? label : '-'}
                </Text>
              </span>
            </Tooltip>
          );
        },
      });
    }

    if (showExecutionRunColumn) {
      result.push({
        field: 'runId',
        headerName: 'Execution Run',
        flex: columnFlex.executionRun,
        align: 'left',
        headerAlign: 'left',
        filterable: false,
        sortable: true,
        renderCell: (params): ReactElement => (
          <ProjectCell label={params.row.runId} href={`/execution-runs/${params.row.runId}`} />
        ),
      });
    }

    result.push({
      field: 'tags',
      headerName: 'Tags',
      flex: columnFlex.tags,
      filterable: false,
      sortable: false,
      renderCell: (params): ReactElement => <TagsInlineCell tags={resolveEntityTags(params.row)} />,
    });

    return result;
  }, [
    durationThreshold,
    setSelectedProjectId,
    showExecutedColumn,
    showExecutionRunColumn,
    showSuiteColumn,
    showTestColumn,
  ]);

  const gridConfig = testExecutionsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'test-executions-table',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });

  const gridProps = buildTableGridProps<TestExecutionDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText,
    onRowsChange,
    onDataUpdatedAt,
    showLastUpdated,
    chrome: 'embedded',
  });

  return (
    <ServerDataTable<TestExecutionDto>
      queryPath="test-executions/query"
      {...gridConfig}
      {...gridProps}
      columns={columns}
      getRowId={(row) => row.id}
      token={session?.tokens.accessToken ?? null}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
    />
  );
};
